import { useState } from 'react';
import type { ForecastResult } from '../types/wasm';

interface ExportResultsProps {
  forecastResult: ForecastResult;
  forecastDates: string[];
  historicalData: Array<{ date: string; value: number }>;
  modelType?: string;
  modelParams?: Record<string, any>;
}

export function ExportResults({ forecastResult, forecastDates, historicalData, modelType, modelParams }: ExportResultsProps) {
  const [copySuccess, setCopySuccess] = useState(false);

  // Epic 10: Enhanced CSV export with confidence intervals and metadata
  const exportToCSV = () => {
    try {
      // Metadata header
      let csv = `# WaySightAI Forecast Export\n`;
      csv += `# Generated: ${new Date().toISOString()}\n`;
      csv += `# Model: ${modelType || 'Unknown'}\n`;
      if (modelParams) {
        csv += `# Parameters: ${JSON.stringify(modelParams)}\n`;
      }
      csv += `# MAPE: ${forecastResult.diagnostics.mape.toFixed(2)}%\n`;
      csv += `# RMSE: ${forecastResult.diagnostics.rmse.toFixed(4)}\n`;
      csv += `# MAE: ${forecastResult.diagnostics.mae.toFixed(4)}\n`;
      if (forecastResult.diagnostics.aic) {
        csv += `# AIC: ${forecastResult.diagnostics.aic.toFixed(2)}\n`;
      }
      if (forecastResult.diagnostics.bic) {
        csv += `# BIC: ${forecastResult.diagnostics.bic.toFixed(2)}\n`;
      }
      csv += `#\n`;

      // Main data header with confidence intervals
      csv += 'Date,Type,Value,Lower_95%_CI,Upper_95%_CI,Fitted_Value,Residual\n';

      // Add historical data with fitted values and residuals
      historicalData.forEach((point, index) => {
        const fitted = forecastResult.fitted_values[index];
        const residual = forecastResult.residuals[index];
        csv += `${point.date},Historical,${point.value},,,${fitted ? fitted.toFixed(6) : ''},${residual ? residual.toFixed(6) : ''}\n`;
      });

      // Add forecast data with confidence intervals
      forecastDates.forEach((date, index) => {
        const forecast = forecastResult.forecasts[index];
        const lower = forecastResult.lower_bounds[index];
        const upper = forecastResult.upper_bounds[index];
        csv += `${date},Forecast,${forecast.toFixed(6)},${lower.toFixed(6)},${upper.toFixed(6)},,\n`;
      });

      // Add diagnostics as separate section
      csv += `\n# Diagnostics\n`;
      csv += `Metric,Value\n`;
      csv += `MAPE,${forecastResult.diagnostics.mape.toFixed(2)}%\n`;
      csv += `RMSE,${forecastResult.diagnostics.rmse.toFixed(4)}\n`;
      csv += `MAE,${forecastResult.diagnostics.mae.toFixed(4)}\n`;
      if (forecastResult.diagnostics.aic) {
        csv += `AIC,${forecastResult.diagnostics.aic.toFixed(2)}\n`;
      }
      if (forecastResult.diagnostics.bic) {
        csv += `BIC,${forecastResult.diagnostics.bic.toFixed(2)}\n`;
      }
      csv += `Execution_Time_ms,${forecastResult.execution_time_ms}\n`;

      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `waysightai_forecast_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  // Epic 10: Enhanced copy to clipboard with better formatting
  const copyToClipboard = async () => {
    try {
      const text = `WaySightAI Forecast Results
Generated: ${new Date().toLocaleString()}
Model: ${modelType || 'Unknown'}

📊 Model Performance:
• MAPE: ${forecastResult.diagnostics.mape.toFixed(2)}%
• RMSE: ${forecastResult.diagnostics.rmse.toFixed(4)}
• MAE: ${forecastResult.diagnostics.mae.toFixed(4)}
${forecastResult.diagnostics.aic ? `• AIC: ${forecastResult.diagnostics.aic.toFixed(2)}` : ''}
${forecastResult.diagnostics.bic ? `• BIC: ${forecastResult.diagnostics.bic.toFixed(2)}` : ''}

🔮 Forecast (${forecastDates.length} periods ahead):
${forecastDates.map((date, i) =>
  `${date}: ${forecastResult.forecasts[i].toFixed(4)} (95% CI: ${forecastResult.lower_bounds[i].toFixed(4)} - ${forecastResult.upper_bounds[i].toFixed(4)})`
).join('\n')}

📈 Historical Performance:
• Data Points: ${historicalData.length}
• Execution Time: ${forecastResult.execution_time_ms.toFixed(2)}ms`;

      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy to clipboard. Please try again.');
    }
  };

  // Epic 10: Enhanced JSON export with complete forecast data
  const exportFullJSON = () => {
    try {
      const fullData = {
        metadata: {
          timestamp: new Date().toISOString(),
          model_type: modelType || 'Unknown',
          model_parameters: modelParams || {},
          execution_time_ms: forecastResult.execution_time_ms,
        },
        diagnostics: {
          mape: forecastResult.diagnostics.mape,
          rmse: forecastResult.diagnostics.rmse,
          mae: forecastResult.diagnostics.mae,
          aic: forecastResult.diagnostics.aic,
          bic: forecastResult.diagnostics.bic,
        },
        historical: {
          count: historicalData.length,
          data: historicalData.map((point, i) => ({
            date: point.date,
            actual: point.value,
            fitted: forecastResult.fitted_values[i],
            residual: forecastResult.residuals[i],
          })),
        },
        forecast: {
          horizon: forecastDates.length,
          data: forecastDates.map((date, i) => ({
            date,
            forecast: forecastResult.forecasts[i],
            lower_95_ci: forecastResult.lower_bounds[i],
            upper_95_ci: forecastResult.upper_bounds[i],
          })),
          summary: {
            first: forecastResult.forecasts[0],
            last: forecastResult.forecasts[forecastResult.forecasts.length - 1],
            average: forecastResult.forecasts.reduce((a, b) => a + b, 0) / forecastResult.forecasts.length,
            min: Math.min(...forecastResult.forecasts),
            max: Math.max(...forecastResult.forecasts),
          },
        },
        residuals: {
          count: forecastResult.residuals.length,
          mean: forecastResult.residuals.reduce((a, b) => a + b, 0) / forecastResult.residuals.length,
          min: Math.min(...forecastResult.residuals),
          max: Math.max(...forecastResult.residuals),
          std_dev: Math.sqrt(
            forecastResult.residuals.reduce((sum, r) => {
              const mean = forecastResult.residuals.reduce((a, b) => a + b, 0) / forecastResult.residuals.length;
              return sum + Math.pow(r - mean, 2);
            }, 0) / forecastResult.residuals.length
          ),
        },
      };

      const json = JSON.stringify(fullData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `waysightai_forecast_${new Date().toISOString().split('T')[0]}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting JSON:', error);
      alert('Failed to export JSON. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Export Results</h2>

      <div className="space-y-4">
        <p className="text-gray-600 text-sm">
          Download your forecast results in various formats for further analysis, reporting, or integration with other tools.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* CSV Export - Enhanced */}
          <button
            onClick={exportToCSV}
            className="flex flex-col items-center p-6 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-lg border-2 border-green-200 transition-all hover:shadow-md"
          >
            <svg
              className="w-12 h-12 text-green-600 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="font-semibold text-gray-900">Export CSV</span>
            <span className="text-xs text-gray-600 mt-1">With CI & metadata</span>
          </button>

          {/* Copy to Clipboard - Enhanced */}
          <button
            onClick={copyToClipboard}
            className="flex flex-col items-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-lg border-2 border-blue-200 transition-all hover:shadow-md relative"
          >
            <svg
              className="w-12 h-12 text-blue-600 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
              />
            </svg>
            <span className="font-semibold text-gray-900">Copy Results</span>
            <span className="text-xs text-gray-600 mt-1">Formatted summary</span>
            {copySuccess && (
              <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                ✓ Copied!
              </div>
            )}
          </button>

          {/* JSON Export - Enhanced */}
          <button
            onClick={exportFullJSON}
            className="flex flex-col items-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-lg border-2 border-purple-200 transition-all hover:shadow-md"
          >
            <svg
              className="w-12 h-12 text-purple-600 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="font-semibold text-gray-900">Export JSON</span>
            <span className="text-xs text-gray-600 mt-1">Complete dataset</span>
          </button>
        </div>

        {/* Info box */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-gray-600 mr-2 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-gray-700">
                <strong>Privacy Note:</strong> All exports are generated locally in your browser.
                Your data never leaves your device - this is client-side only processing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
